import { useEffect, useMemo, useRef, useState } from 'react';
import { TRIGGER_TYPE_DEFS } from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import { askConfirm, errorText, showError } from '../lib/dialogs.js';
import { useFetch } from '../lib/useFetch.js';
import { providerName, useProviders } from '../lib/providers.js';
import { Async } from '../components/States.js';
import { Modal } from '../components/Modal.js';
import { Dd } from '../components/Dropdown.js';
import { DateField } from '../components/DateTimeField.js';
import { BundleEditor } from '../components/BundleEditor.js';
import { useSession, canManage } from '../auth/session.js';
import { isSystemForm } from '../lib/systemForms.js';
import {
  bundleHints,
  ruleCatalogForms,
  ruleSaveHint,
  ruleSaveMissing,
  type RuleBundleRow,
  type RuleFormRow,
} from '../lib/formRulesCatalog.js';

/* ==========================================================
   Mockup structural constants (ehr-revised Forms Hub port)
   ========================================================== */
const NEEDS_OFFSET = new Set(['offset_before', 'offset_after', 'appt_before', 'appt_after']);
const NEEDS_DATE = new Set(['specific_date']);
const NEEDS_EVENT = new Set([
  'admission',
  'enrollment',
  'discharge',
  'appointment',
  'offset_before',
  'offset_after',
  'date_field',
  'loc_change',
  'no_show',
  'form_complete',
  'crisis_flag',
  'referral_received',
  'med_change',
  'insurance_verify',
  'tx_plan_due',
  'staff_assign',
]);
const NEEDS_APPT = new Set(['appt_before', 'appt_after']);
const NO_RECURRING = new Set([
  'specific_date',
  'manual',
  'one_time',
  'form_complete',
  'crisis_flag',
]);

const TRIGGER_EVENT_PLACEHOLDER: Record<string, string> = {
  admission: 'e.g. PHP, IOP',
  enrollment: 'e.g. MAT, Detox',
  discharge: 'e.g. PHP',
  appointment: 'e.g. Intake Visit',
  offset_before: 'e.g. admission',
  offset_after: 'e.g. discharge',
  date_field: 'e.g. Next Review Date',
  loc_change: 'e.g. PHP → IOP',
  no_show: 'e.g. Individual Therapy',
  form_complete: 'e.g. PHQ-9',
  crisis_flag: 'e.g. High Risk',
  referral_received: 'e.g. Physician Referral',
  med_change: 'e.g. Buprenorphine',
  insurance_verify: 'e.g. Medicaid',
  tx_plan_due: 'e.g. 90-Day Review',
  staff_assign: 'e.g. Assigned Clinician',
};
const TRIGGER_EVENT_SUGGESTIONS: Record<string, string[]> = {
  admission: ['IOP', 'PHP', 'Residential', 'MAT', 'Detox'],
  enrollment: ['MAT', 'Detox', 'IOP', 'PHP', 'Residential'],
  discharge: ['IOP', 'PHP', 'Residential', 'Detox', 'MAT'],
  appointment: [
    'Intake Assessment',
    'Individual Therapy',
    'Group Therapy',
    'Med Management',
    'Psychiatry Evaluation',
  ],
  offset_before: ['admission', 'appointment', 'discharge', 'enrollment', 'next session'],
  offset_after: ['discharge', 'admission', 'enrollment', 'appointment', 'assessment'],
  date_field: [
    'Date of Birth',
    'Consent Signed Date',
    'Next Review Date',
    'Authorization Expiry',
    'Insurance Effective Date',
  ],
  loc_change: ['PHP → IOP', 'IOP → OP', 'Residential → PHP', 'OP → IOP', 'MAT Step-Down'],
  no_show: [
    'Individual Therapy',
    'Group Therapy',
    'Med Management',
    'Intake Assessment',
    'Psychiatry Evaluation',
  ],
  form_complete: [
    'PHQ-9 Depression Screener',
    'C-SSRS Suicide Risk',
    'Intake Assessment',
    'Treatment Plan Review',
    'Annual Wellness Survey',
  ],
  crisis_flag: [
    'High Risk',
    'Moderate Risk',
    'Imminent Risk',
    'Elevated PHQ-9',
    'Columbia Level 4-5',
  ],
  referral_received: [
    'Physician Referral',
    'Self-Referral',
    'Court-Ordered',
    'Hospital Discharge',
    'Community Outreach',
  ],
  med_change: ['Buprenorphine', 'Naltrexone', 'Methadone', 'Antidepressant', 'Mood Stabilizer'],
  insurance_verify: ['Medicaid', 'Medicare', 'TRICARE', 'Commercial Insurance', 'No Insurance'],
  tx_plan_due: [
    '30-Day Review',
    '60-Day Review',
    '90-Day Review',
    'Annual Review',
    'Discharge Plan',
  ],
  staff_assign: [
    'Assigned Clinician',
    'Case Manager',
    'Peer Support Specialist',
    'Psychiatrist',
    'Supervisor',
  ],
};
const APPT_TYPES: { value: string; label: string }[] = [
  { value: 'intake', label: 'Intake Assessment' },
  { value: 'individual', label: 'Individual Therapy' },
  { value: 'group', label: 'Group Therapy' },
  { value: 'med_mgmt', label: 'Med Management' },
  { value: 'psychiatry', label: 'Psychiatry Evaluation' },
  { value: 'family', label: 'Family Therapy' },
  { value: 'case_mgmt', label: 'Case Management' },
  { value: 'peer_support', label: 'Peer Support' },
  { value: 'discharge', label: 'Discharge Planning' },
  { value: 'followup', label: 'Follow-up Visit' },
  { value: 'telehealth', label: 'Telehealth Session' },
  { value: 'crisis', label: 'Crisis Intervention' },
  { value: 'every', label: 'Every appointment (any type)' },
];
const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'clinician', label: 'Clinician' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'front_desk', label: 'Front Desk' },
];
const COND_LABELS: Record<string, string> = {
  one_time: 'One-time send',
  specific_date: 'On specific date',
  admission: 'Upon admission',
  enrollment: 'Upon enrollment into program',
  discharge: 'Upon discharge / episode closure',
  appointment: 'On appointment type',
  manual: 'Manual staff trigger',
  recurring: 'Recurring',
  auth_approval: 'Upon authorization approval',
  loc_change: 'Upon level of care change',
  no_show: 'Upon no-show / cancellation',
  form_complete: 'Upon form completion',
  med_change: 'Upon medication change',
  crisis_flag: 'Upon crisis flag',
  insurance_verify: 'Upon insurance verification',
  referral_received: 'Upon referral received',
  tx_plan_due: 'Upon treatment plan due / expiring',
  staff_assign: 'Upon staff assignment',
};

const range = (from: number, to: number) =>
  Array.from({ length: to - from + 1 }, (_, i) => from + i);
const DAY_OPTS_0 = range(0, 365).map((i) => ({ value: i, label: i === 1 ? '1 Day' : `${i} Days` }));
const HOUR_OPTS = range(0, 23).map((i) => ({
  value: i,
  label: `${String(i).padStart(2, '0')} hr`,
}));
const MIN_OPTS = range(0, 59).map((i) => ({ value: i, label: `${i} min` }));
// String-valued mirrors for the Dd component (its value is a string).
const DAY_DD_0 = DAY_OPTS_0.map((o) => ({ value: String(o.value), label: o.label }));
const DAY_DD_1 = DAY_DD_0.slice(1);
const HOUR_DD = HOUR_OPTS.map((o) => ({ value: String(o.value), label: o.label }));
const MIN_DD = MIN_OPTS.map((o) => ({ value: String(o.value), label: o.label }));

const DOW_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DOW_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/* ── Workflow modal (Notifications & Workflow) — mirrors the mockup's TRIGGER_DEFS/autoRules.
      alertshub#4: saved on the server (GET / PUT /practice/forms-workflow), and the Forms Hub's
      Alerts Hub cards follow these switches. Jay 2026-10-12: every switch starts on. ── */
const TRIGGER_DEFS = [
  {
    id: 'submitted',
    label: 'Form submitted',
    desc: 'Notify when a client completes and submits this form.',
    hasDelay: false,
    delayLabel: '',
  },
  {
    id: 'incomplete',
    label: 'Opened but not completed',
    desc: 'Notify if opened but not submitted within a set time.',
    hasDelay: true,
    delayLabel: 'Notify after',
  },
  {
    id: 'signoff_complete',
    label: 'Signoff completed',
    desc: 'Notify when a clinician or supervisor signs off.',
    hasDelay: false,
    delayLabel: '',
  },
  {
    id: 'signoff_overdue',
    label: 'Signoff overdue',
    desc: 'Notify if signoff is not completed by the due date.',
    hasDelay: false,
    delayLabel: '',
  },
  {
    id: 'assigned',
    label: 'Form assigned to client',
    desc: 'Notify when this form (or a bundle containing it) is sent.',
    hasDelay: false,
    delayLabel: '',
  },
  {
    id: 'completion_overdue',
    label: 'Form past completion deadline',
    desc: 'Notify staff when an assigned form passes its completion deadline.',
    hasDelay: false,
    delayLabel: '',
  },
];
interface AutoRule {
  id: string;
  on: boolean;
  trigger: string;
  target: string;
}
interface WorkflowSettings {
  /** A trigger missing here is on (the server's rule too). */
  triggerToggles: Record<string, boolean>;
  /** "Opened but not completed": how long before the sender hears ('24 hours' when unset). */
  delays: Record<string, string>;
  signoffType: 'none' | 'clinician' | 'cosign' | 'clinician_only';
  autoRules: AutoRule[];
}
const defaultWorkflow = (): WorkflowSettings => ({
  triggerToggles: {},
  delays: {},
  signoffType: 'none',
  autoRules: [{ id: 'ar1', on: true, trigger: 'intake_started', target: 'portal' }],
});
/** alertshub#4: on unless saved off (a practice that never saved has every alert on). */
const triggerOn = (wf: Pick<WorkflowSettings, 'triggerToggles'>, id: string) =>
  wf.triggerToggles[id] !== false;

/* ==========================================================
   Shared shapes
   ========================================================== */
interface Named {
  id: string;
  name: string;
  description?: string | null;
}
interface OrgUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}
const orgUserName = (u: OrgUser) => [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;

interface CatalogItem {
  id: string;
  name: string;
  type: 'form' | 'packet';
  desc: string;
  /** credifyfbs#302: tells same-named bundles apart (form count, created date). */
  hint?: string;
}

interface RecObj {
  freq: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number;
  daysOfWeek: number[];
  monthDay: number;
  ends: 'never' | 'on' | 'after';
  endDate: string;
  endCount: number;
}
const defaultRec = (): RecObj => ({
  freq: 'weekly',
  interval: 1,
  daysOfWeek: [1],
  monthDay: 1,
  ends: 'never',
  endDate: '',
  endCount: 10,
});

interface Cond {
  id: string;
  type: string;
  event: string;
  amount: number;
  minutes: number;
  apptType: string;
  dateVal: string;
  joiner: 'AND' | 'OR' | null;
  recurring: boolean;
  recObj: RecObj | null;
}
const newCond = (first: boolean, id: string): Cond => ({
  id,
  // #111 — start unselected ("Select one…") so the rule author picks a trigger
  // deliberately instead of silently defaulting to "Upon admission".
  type: '',
  event: '',
  amount: 3,
  minutes: 0,
  apptType: 'every',
  dateVal: '',
  joiner: first ? null : 'AND',
  recurring: false,
  recObj: null,
});

interface Deadline {
  enabled: boolean;
  days: number;
  hours: number;
  mins: number;
  remindEnabled: boolean;
  remindDays: number;
  remindHours: number;
  remindMins: number;
}
const emptyDeadline = (): Deadline => ({
  enabled: false,
  days: 1,
  hours: 0,
  mins: 0,
  remindEnabled: false,
  remindDays: 1,
  remindHours: 0,
  remindMins: 0,
});
const isZeroDeadline = (d: Deadline) => d.days + d.hours + d.mins <= 0;
const deadlineLabel = (d: Deadline): string => {
  if (d.days === 1 && d.hours === 0 && d.mins === 0) return '24 hr';
  const parts: string[] = [];
  if (d.days) parts.push(`${d.days}d`);
  if (d.hours) parts.push(`${d.hours}h`);
  if (d.mins) parts.push(`${d.mins}m`);
  return parts.join(' ') || '0m';
};

interface SignoffObligation {
  enabled: boolean;
  remindEnabled: boolean;
  remindDays: number;
  remindHours: number;
  remindMins: number;
  escalateEnabled: boolean;
  escalateDays: number;
  escalateHours: number;
  escalateMins: number;
  supervisorSrc: 'org_chart' | 'role' | 'user';
  supervisorRole: string;
  supervisorUser: string;
  chInApp: boolean;
  chEmail: boolean;
  chSecure: boolean;
}
const defaultSO = (): SignoffObligation => ({
  enabled: false,
  remindEnabled: false,
  remindDays: 1,
  remindHours: 0,
  remindMins: 0,
  escalateEnabled: false,
  escalateDays: 3,
  escalateHours: 0,
  escalateMins: 0,
  supervisorSrc: 'org_chart',
  supervisorRole: '',
  supervisorUser: '',
  chInApp: true,
  chEmail: true,
  chSecure: true,
});

/* API shapes */
interface ApiCondition {
  type: string;
  event: string | null;
  amount: number | null;
  unit: string | null;
  direction: string | null;
  recurring: Record<string, unknown> | null;
  joiner: string;
}
interface ApiRule {
  id: string;
  name: string;
  status: string;
  triggerSummary: string;
  deadline: Partial<Deadline> | null;
  signoffObligation: Partial<SignoffObligation> | null;
  conditions: ApiCondition[];
  recipients: { kind: string; value: string | null }[];
  filters: { dimension: string; value: string }[];
  forms: { form: { id: string; name: string } }[];
}

/* Human label for a condition (mockup buildCondLabel, adapted to both the
   builder shape and the API row shape). */
function condLabel(c: {
  type: string;
  event?: string | null;
  amount?: number | null;
  unit?: string | null;
  minutes?: number;
  apptType?: string;
  dateVal?: string;
  recurring?: boolean | Record<string, unknown> | null;
  recObj?: RecObj | null;
}): string {
  const dir = c.type === 'offset_before' || c.type === 'appt_before' ? 'before' : 'after';
  const minsStr = c.minutes && c.minutes > 0 ? ` and ${c.minutes} min` : '';
  const rec =
    typeof c.recurring === 'object' && c.recurring
      ? ` (recurring every ${(c.recurring as { interval?: number }).interval ?? 1} ${(c.recurring as { freq?: string }).freq ?? ''})`.replace(
          /\s+\)/,
          ')',
        )
      : c.recurring === true
        ? c.recObj
          ? ` (recurring every ${c.recObj.interval} ${c.recObj.freq})`
          : ' (recurring)'
        : '';
  const amount = c.amount ?? 1;
  const unit = c.unit ?? 'days';
  if (NEEDS_APPT.has(c.type)) {
    const appt =
      (c.apptType
        ? APPT_TYPES.find((o) => o.value === c.apptType)?.label
        : (c.event ?? undefined)) ?? 'appointment';
    return `${amount} ${unit}${minsStr} ${dir} ${appt}${rec}`;
  }
  if (NEEDS_OFFSET.has(c.type)) {
    return `${amount} ${unit}${minsStr} ${dir}${c.event ? ' ' + c.event : ' event'}${rec}`;
  }
  const base = COND_LABELS[c.type] ?? c.type;
  const ev = c.dateVal || c.event;
  return base + (ev ? ' → ' + ev : '') + rec;
}

const CircleNum = ({ n }: { n: number }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="var(--green-primary)"
    style={{ flex: 'none' }}
  >
    <circle cx="12" cy="12" r="10" />
    <text
      x="12"
      y="16"
      textAnchor="middle"
      fontFamily="Sora,sans-serif"
      fontSize="11"
      fontWeight="700"
      fill="white"
    >
      {n}
    </text>
  </svg>
);

const PencilSvg = ({ size = 13 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ verticalAlign: -1 }}
  >
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);
const BoltSvg = ({ size = 13 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);
const BellSvg = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

/* ==========================================================
   Recurrence panel (mockup makeRecurrencePanel port)
   ========================================================== */
function RecPanel({ value, onChange }: { value: RecObj; onChange: (r: RecObj) => void }) {
  const rec = value;
  const set = (patch: Partial<RecObj>) => onChange({ ...rec, ...patch });
  const freqs: RecObj['freq'][] = ['daily', 'weekly', 'monthly', 'yearly'];
  const freqLabels = ['Daily', 'Weekly', 'Monthly', 'Yearly'];
  const unitLabel =
    { daily: 'day', weekly: 'week', monthly: 'month', yearly: 'year' }[rec.freq] +
    (rec.interval === 1 ? '' : 's');
  return (
    <div className="rec-panel show" style={{ width: '100%', marginTop: 8 }}>
      <div className="rec-row">
        <span className="rec-label">Repeats</span>
        <div className="rec-freq-seg">
          {freqs.map((f, i) => (
            <button
              key={f}
              type="button"
              className={`rec-freq-btn${rec.freq === f ? ' active' : ''}`}
              onClick={() =>
                set({ freq: f, daysOfWeek: rec.daysOfWeek.length ? rec.daysOfWeek : [1] })
              }
            >
              {freqLabels[i]}
            </button>
          ))}
        </div>
      </div>
      <div className="rec-row">
        <span className="rec-label">Every</span>
        <input
          type="number"
          className="rec-num-input"
          min={1}
          max={99}
          value={rec.interval}
          onChange={(e) => set({ interval: Math.max(1, parseInt(e.target.value) || 1) })}
        />
        <span className="rec-unit-label">{unitLabel}</span>
      </div>
      {rec.freq === 'weekly' && (
        <div className="rec-row">
          <span className="rec-label">On</span>
          <div className="rec-dow-row">
            {DOW_LABELS.map((d, i) => (
              <button
                key={i}
                type="button"
                className={`rec-dow-chip${rec.daysOfWeek.includes(i) ? ' on' : ''}`}
                title={DOW_FULL[i]}
                onClick={() => {
                  const has = rec.daysOfWeek.includes(i);
                  if (has && rec.daysOfWeek.length <= 1) return;
                  set({
                    daysOfWeek: has
                      ? rec.daysOfWeek.filter((x) => x !== i)
                      : [...rec.daysOfWeek, i],
                  });
                }}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      )}
      {rec.freq === 'monthly' && (
        <div className="rec-row">
          <span className="rec-label">On day</span>
          <input
            type="number"
            className="rec-num-input"
            min={1}
            max={31}
            value={rec.monthDay}
            onChange={(e) =>
              set({ monthDay: Math.min(31, Math.max(1, parseInt(e.target.value) || 1)) })
            }
          />
          <span className="rec-unit-label">of the month</span>
        </div>
      )}
      <div className="rec-row" style={{ marginBottom: 6 }}>
        <span className="rec-label">Ends</span>
        <div className="rec-ends-seg">
          {(['never', 'on', 'after'] as const).map((e) => (
            <button
              key={e}
              type="button"
              className={`rec-ends-btn${rec.ends === e ? ' active' : ''}`}
              onClick={() => set({ ends: e })}
            >
              {e === 'never' ? 'Never' : e === 'on' ? 'On date' : 'After'}
            </button>
          ))}
        </div>
      </div>
      {rec.ends === 'on' && (
        <div className="rec-ends-detail">
          <DateField value={rec.endDate} onChange={(v) => set({ endDate: v })} />
        </div>
      )}
      {rec.ends === 'after' && (
        <div className="rec-ends-detail">
          <input
            type="number"
            className="rec-num-input"
            min={1}
            value={rec.endCount}
            onChange={(e) => set({ endCount: Math.max(1, parseInt(e.target.value) || 1) })}
          />
          <span className="rec-unit-label">occurrences</span>
        </div>
      )}
    </div>
  );
}

/* ==========================================================
   Signoff Obligation modal (mockup so-modal port; persisted per-rule
   via the rule's signoffObligation JSONB — full schema support)
   ========================================================== */
function SignoffObligationModal({
  value,
  users,
  onApply,
  onClose,
}: {
  value: SignoffObligation;
  users: OrgUser[];
  onApply: (so: SignoffObligation) => void;
  onClose: () => void;
}) {
  const [so, setSo] = useState<SignoffObligation>(value);
  const set = (patch: Partial<SignoffObligation>) => setSo((s) => ({ ...s, ...patch }));
  const timeSelects = (
    prefix: 'remind' | 'escalate',
    disabled: boolean,
  ): [number, number, number, (k: 'Days' | 'Hours' | 'Mins', v: number) => void] => {
    const d = prefix === 'remind' ? so.remindDays : so.escalateDays;
    const h = prefix === 'remind' ? so.remindHours : so.escalateHours;
    const m = prefix === 'remind' ? so.remindMins : so.escalateMins;
    void disabled;
    return [d, h, m, (k, v) => set({ [`${prefix}${k}`]: v } as Partial<SignoffObligation>)];
  };
  const [rd, rh, rm, setRemind] = timeSelects('remind', !so.remindEnabled);
  const [ed, eh, em, setEsc] = timeSelects('escalate', !so.escalateEnabled);
  const override = so.supervisorSrc !== 'org_chart';

  return (
    <Modal
      title="Configure Signoff Obligation"
      className="so-modal"
      onClose={onClose}
      actions={
        <>
          <button className="fh-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="fh-btn primary" onClick={() => onApply(so)}>
            Apply to Rule
          </button>
        </>
      }
    >
      <div className="so-enabled-row">
        <button
          type="button"
          className={`so-enabled-toggle${so.enabled ? ' on' : ''}`}
          aria-pressed={so.enabled}
          onClick={() => set({ enabled: !so.enabled })}
        />
        <span className="so-enabled-label">
          Require provider signoff on every send of this form
        </span>
      </div>

      <div className={so.enabled ? undefined : 'so-body-disabled'}>
        {/* Provider Reminder */}
        <div className="so-section">
          <div className="so-section-title">Provider Reminder</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            After the form is sent, remind the assigned provider to sign. Repeats until a final
            sign-off is recorded. Saving as a draft does <strong>not</strong> stop reminders.
          </div>
          <div className="so-time-row">
            <input
              type="checkbox"
              className="so-time-chk"
              checked={so.remindEnabled}
              onChange={(e) => set({ remindEnabled: e.target.checked })}
            />
            <span className="so-time-label">Remind every</span>
            <div className={`so-cdds-wrap${so.remindEnabled ? '' : ' disabled'}`}>
              <Dd
                value={String(rd)}
                onChange={(v) => setRemind('Days', Number(v))}
                ariaLabel="Remind days"
                options={DAY_DD_0}
              />
              <Dd
                value={String(rh)}
                onChange={(v) => setRemind('Hours', Number(v))}
                ariaLabel="Remind hours"
                options={HOUR_DD}
              />
              <Dd
                value={String(rm)}
                onChange={(v) => setRemind('Mins', Number(v))}
                ariaLabel="Remind minutes"
                options={MIN_DD}
              />
            </div>
          </div>
        </div>

        {/* Supervisor Escalation */}
        <div className="so-section">
          <div className="so-section-title">Supervisor Escalation</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            If the provider has not submitted a final sign-off by the deadline below, the supervisor
            is notified. Draft saves do <strong>not</strong> stop the escalation clock.
          </div>
          <div className="so-time-row" style={{ marginBottom: 14 }}>
            <input
              type="checkbox"
              className="so-time-chk"
              checked={so.escalateEnabled}
              onChange={(e) => set({ escalateEnabled: e.target.checked })}
            />
            <span className="so-time-label">Notify supervisor after</span>
            <div className={`so-cdds-wrap${so.escalateEnabled ? '' : ' disabled'}`}>
              <Dd
                value={String(ed)}
                onChange={(v) => setEsc('Days', Number(v))}
                ariaLabel="Escalate days"
                options={DAY_DD_0}
              />
              <Dd
                value={String(eh)}
                onChange={(v) => setEsc('Hours', Number(v))}
                ariaLabel="Escalate hours"
                options={HOUR_DD}
              />
              <Dd
                value={String(em)}
                onChange={(v) => setEsc('Mins', Number(v))}
                ariaLabel="Escalate minutes"
                options={MIN_DD}
              />
            </div>
          </div>

          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '.04em',
              marginBottom: 10,
            }}
          >
            Supervisor Source
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name="soSupervisorSrc"
                checked={!override}
                onChange={() => set({ supervisorSrc: 'org_chart' })}
                style={{ accentColor: 'var(--green-primary)', width: 'auto' }}
              />
              <span>
                <strong>Org-chart default</strong> — use the{' '}
                <code
                  style={{
                    fontSize: 11.5,
                    background: 'var(--surface)',
                    padding: '1px 5px',
                    borderRadius: 4,
                  }}
                >
                  supervisorId
                </code>{' '}
                on the assigned provider's record
              </span>
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name="soSupervisorSrc"
                checked={override}
                onChange={() => set({ supervisorSrc: 'role' })}
                style={{ accentColor: 'var(--green-primary)', width: 'auto' }}
              />
              <span>
                <strong>Override for this rule</strong> — specify a role or user below
              </span>
            </label>
          </div>
          {override && (
            <div
              style={{
                marginTop: 12,
                padding: '12px 14px',
                background: 'var(--mint-pale)',
                border: '1px solid var(--mint-soft)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <div className="so-supervisor-row">
                <span className="so-supervisor-label">By Role</span>
                <Dd
                  value={so.supervisorRole}
                  onChange={(v) => set({ supervisorRole: v, supervisorSrc: 'role' })}
                  searchable={false}
                  ariaLabel="Supervisor role"
                  options={[
                    { value: '', label: '— Any supervisor role —' },
                    { value: 'supervisor', label: 'Supervisor' },
                  ]}
                />
              </div>
              <div className="so-supervisor-row" style={{ alignItems: 'center' }}>
                <span
                  className="so-supervisor-label"
                  style={{ fontSize: 12, color: 'var(--text-muted)' }}
                >
                  or specific user
                </span>
                <Dd
                  value={so.supervisorUser}
                  onChange={(v) =>
                    set({
                      supervisorUser: v,
                      supervisorSrc: v ? 'user' : 'role',
                    })
                  }
                  ariaLabel="Supervisor user"
                  placeholder="— Select user —"
                  options={[
                    { value: '', label: '— Select user —' },
                    ...users.map((u) => ({ value: u.id, label: orgUserName(u) })),
                  ]}
                />
              </div>
            </div>
          )}
        </div>

        {/* Notification Channels */}
        <div className="so-section">
          <div className="so-section-title">Notification Channels</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            Applies to both provider reminders and supervisor escalation notifications.
          </div>
          <div className="so-channel-row">
            <input
              type="checkbox"
              checked={so.chInApp}
              onChange={(e) => set({ chInApp: e.target.checked })}
            />
            <div className="so-channel-icon inapp">
              <BellSvg />
            </div>
            <div className="so-channel-body">
              <div className="so-channel-name">In-App Notification</div>
              <div className="so-channel-desc">
                Appears in the Notification Log and notification bell
              </div>
            </div>
          </div>
          <div className="so-channel-row">
            <input
              type="checkbox"
              checked={so.chEmail}
              onChange={(e) => set({ chEmail: e.target.checked })}
            />
            <div className="so-channel-icon email">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <div className="so-channel-body">
              <div className="so-channel-name">Email</div>
              <div className="so-channel-desc">
                Sent to provider's and supervisor's email on file
              </div>
            </div>
          </div>
          <div className="so-channel-row">
            <input
              type="checkbox"
              checked={so.chSecure}
              onChange={(e) => set({ chSecure: e.target.checked })}
            />
            <div className="so-channel-icon secure">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <div className="so-channel-body">
              <div className="so-channel-name">Secure Message</div>
              <div className="so-channel-desc">
                Delivered via the Secure Messaging module (Matrix)
              </div>
            </div>
          </div>
        </div>

        <div className="so-draft-note">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>
            <strong>Draft saves do not satisfy the signoff obligation.</strong> Only a final
            attestation/signature stops the reminder and escalation clocks. Draft activity is logged
            in the Signoff Queue for visibility.
          </span>
        </div>
      </div>
    </Modal>
  );
}

/* ==========================================================
   Notifications & Workflow modal (client-side settings; the
   backend PUT /workflow/settings endpoint is deferred)
   ========================================================== */
function WorkflowModal({ onClose, canSave }: { onClose: () => void; canSave: boolean }) {
  const [wf, setWf] = useState<WorkflowSettings>(defaultWorkflow);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [soDraft, setSoDraft] = useState<Record<string, string>>({});
  // alertshub#4: the practice's saved switches (the server fills defaults: every alert on)
  useEffect(() => {
    let live = true;
    api
      .get<WorkflowSettings>('/practice/forms-workflow')
      .then((row) => {
        if (live) setWf({ ...defaultWorkflow(), ...row });
      })
      .catch(() => {})
      .finally(() => {
        if (live) setLoaded(true);
      });
    return () => {
      live = false;
    };
  }, []);
  // #35 — clinician pickers pull real org users from the DB (were hardcoded); ehr#232: providers
  // only (they see clients).
  const providers = useProviders();
  const clinicianDdOptions = [
    { value: '', label: 'Assigned Clinician' },
    ...(providers.data ?? []).map((p) => ({ value: p.id, label: providerName(p) })),
  ];
  // The Signoff Workflow selects are presentational (not persisted with `wf`);
  // this ephemeral bag keeps them controlled so they can render as custom Dds.
  const soVal = (k: string, dflt: string) => soDraft[k] ?? dflt;
  const soSet = (k: string) => (v: string) => setSoDraft((d) => ({ ...d, [k]: v }));
  const save = () => {
    setSaving(true);
    api
      .put('/practice/forms-workflow', wf)
      .then(() => onClose())
      .catch((e) => showError(errorText(e, 'Could not save the notification settings')))
      .finally(() => setSaving(false));
  };
  const TL: Record<string, string> = {
    intake_started: 'Intake started',
    admission: 'Client admitted',
    treatment_plan_signed: 'Treatment plan signed',
    manual_only: 'Manual send only',
  };
  const TT: Record<string, string> = {
    portal: 'Client Portal',
    queue: 'Clinician Queue',
    both: 'Both',
  };
  const setRule = (id: string, patch: Partial<AutoRule>) =>
    setWf((w) => ({
      ...w,
      autoRules: w.autoRules.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    }));

  return (
    <Modal
      title="Notifications & Workflow"
      className="fh-wide"
      onClose={onClose}
      actions={
        <>
          <button className="fh-btn" onClick={onClose}>
            Cancel
          </button>
          {!canSave && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 'auto' }}>
              Only admins and managers can change these.
            </span>
          )}
          <button
            className="fh-btn primary"
            onClick={save}
            disabled={!canSave || !loaded || saving}
            aria-busy={saving}
          >
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </>
      }
    >
      <div className="wf-section">
        <div className="wf-section-title">
          <BellSvg /> Notification Triggers
        </div>
        <div>
          {TRIGGER_DEFS.map((t) => {
            const on = triggerOn(wf, t.id);
            return (
              <div className="trigger-row" key={t.id}>
                <button
                  type="button"
                  className={`trigger-toggle${on ? ' on' : ''}`}
                  aria-pressed={on}
                  aria-label={t.label}
                  onClick={() =>
                    setWf((w) => ({
                      ...w,
                      triggerToggles: { ...w.triggerToggles, [t.id]: !on },
                    }))
                  }
                />
                <div className="trigger-body">
                  <div className="trigger-label">{t.label}</div>
                  <div className="trigger-desc">{t.desc}</div>
                  <div className={`trigger-extras${on ? ' show' : ''}`}>
                    {t.hasDelay && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <label
                          style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}
                        >
                          {t.delayLabel || 'After'}
                        </label>
                        <Dd
                          value={wf.delays[t.id] ?? '24 hours'}
                          onChange={(v) =>
                            setWf((w) => ({ ...w, delays: { ...w.delays, [t.id]: v } }))
                          }
                          searchable={false}
                          ariaLabel="Delay"
                          options={['24 hours', '48 hours', '3 days', '7 days'].map((o) => ({
                            value: o,
                            label: o,
                          }))}
                        />
                      </div>
                    )}
                    <div className="recipients-row">
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
                        Notify:
                      </span>
                      <label className="channel-chk">
                        <input type="checkbox" defaultChecked /> Assigned Clinician
                      </label>
                      <label className="channel-chk">
                        <input type="checkbox" /> Admin
                      </label>
                      <label className="channel-chk">
                        <input type="checkbox" /> Client
                      </label>
                    </div>
                    <div className="recipients-row">
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
                        Via:
                      </span>
                      <label className="channel-chk">
                        <input type="checkbox" defaultChecked /> Email
                      </label>
                      <label className="channel-chk">
                        <input type="checkbox" /> SMS
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="wf-section">
        <div className="wf-section-title">
          <PencilSvg size={14} /> Signoff Workflow
        </div>
        {(
          [
            ['none', 'No signoff required', 'Form is complete once submitted.'],
            ['clinician', 'Client submits → Clinician signs', 'One-stage signoff.'],
            ['cosign', 'Client submits → Clinician → Supervisor co-signs', 'Two-stage signoff.'],
            ['clinician_only', 'Clinician fills out and signs', 'No client submission required.'],
          ] as const
        ).map(([val, label, desc]) => (
          <div
            className="signoff-option"
            key={val}
            onClick={() => setWf((w) => ({ ...w, signoffType: val }))}
          >
            <input
              type="radio"
              name="signoffType"
              value={val}
              checked={wf.signoffType === val}
              onChange={() => setWf((w) => ({ ...w, signoffType: val }))}
            />
            <div className="signoff-option-body">
              <div className="signoff-option-label">{label}</div>
              <div className="signoff-option-desc">{desc}</div>
              {val === 'clinician' && (
                <div className={`signoff-config${wf.signoffType === 'clinician' ? ' show' : ''}`}>
                  <div className="field-row" style={{ marginBottom: 0 }}>
                    <div className="field-group">
                      <label>Clinician Role or User</label>
                      <Dd
                        value={soVal('clin-clinician', '')}
                        onChange={soSet('clin-clinician')}
                        ariaLabel="Clinician Role or User"
                        options={clinicianDdOptions}
                      />
                    </div>
                    <div className="field-group">
                      <label>Must sign within</label>
                      <Dd
                        value={soVal('clin-within', '48 hours')}
                        onChange={soSet('clin-within')}
                        searchable={false}
                        ariaLabel="Must sign within"
                        options={['24 hours', '48 hours', '3 days', '7 days'].map((o) => ({
                          value: o,
                          label: o,
                        }))}
                      />
                    </div>
                  </div>
                </div>
              )}
              {val === 'cosign' && (
                <div className={`signoff-config${wf.signoffType === 'cosign' ? ' show' : ''}`}>
                  <div className="field-row">
                    <div className="field-group">
                      <label>Clinician</label>
                      <Dd
                        value={soVal('cosign-clinician', '')}
                        onChange={soSet('cosign-clinician')}
                        ariaLabel="Clinician"
                        options={clinicianDdOptions}
                      />
                    </div>
                    <div className="field-group">
                      <label>Sign within</label>
                      <Dd
                        value={soVal('cosign-within', '48 hours')}
                        onChange={soSet('cosign-within')}
                        searchable={false}
                        ariaLabel="Sign within"
                        options={['24 hours', '48 hours', '3 days'].map((o) => ({
                          value: o,
                          label: o,
                        }))}
                      />
                    </div>
                  </div>
                  <div className="field-row" style={{ marginBottom: 0 }}>
                    <div className="field-group">
                      <label>Supervisor</label>
                      <Dd
                        value={soVal('cosign-supervisor', 'Org-chart supervisor')}
                        onChange={soSet('cosign-supervisor')}
                        searchable={false}
                        ariaLabel="Supervisor"
                        options={[{ value: 'Org-chart supervisor', label: 'Org-chart supervisor' }]}
                      />
                    </div>
                    <div className="field-group">
                      <label>Co-sign within</label>
                      <Dd
                        value={soVal('cosign-cowithin', '48 hours')}
                        onChange={soSet('cosign-cowithin')}
                        searchable={false}
                        ariaLabel="Co-sign within"
                        options={['48 hours', '3 days', '5 days'].map((o) => ({
                          value: o,
                          label: o,
                        }))}
                      />
                    </div>
                  </div>
                </div>
              )}
              {val === 'clinician_only' && (
                <div
                  className={`signoff-config${wf.signoffType === 'clinician_only' ? ' show' : ''}`}
                >
                  <div className="field-row" style={{ marginBottom: 0 }}>
                    <div className="field-group">
                      <label>Assigned Clinician</label>
                      <Dd
                        value={soVal('conly-clinician', 'Assigned Clinician')}
                        onChange={soSet('conly-clinician')}
                        searchable={false}
                        ariaLabel="Assigned Clinician"
                        options={[{ value: 'Assigned Clinician', label: 'Assigned Clinician' }]}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="wf-section">
        <div className="wf-section-title">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 2L11 13" />
            <path d="M22 2L15 22l-4-9-9-4 20-7z" />
          </svg>{' '}
          Auto-Dispatch Rules
        </div>
        <div>
          {wf.autoRules.length === 0 ? (
            <p style={{ color: 'var(--text-muted-2)', fontSize: 13 }}>No auto-dispatch rules.</p>
          ) : (
            wf.autoRules.map((r) => (
              <div className="auto-rule" key={r.id}>
                <div className="auto-rule-head">
                  <button
                    type="button"
                    className={`trigger-toggle${r.on ? ' on' : ''}`}
                    aria-pressed={r.on}
                    aria-label="Toggle rule"
                    onClick={() => setRule(r.id, { on: !r.on })}
                  />
                  <div className="rule-label">
                    {TL[r.trigger] ?? r.trigger} → {TT[r.target] ?? r.target}
                  </div>
                  <button
                    className="btn sm danger"
                    onClick={() =>
                      setWf((w) => ({ ...w, autoRules: w.autoRules.filter((x) => x.id !== r.id) }))
                    }
                  >
                    Remove
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <div className="field-group" style={{ minWidth: 160, maxWidth: 220 }}>
                    <label>Trigger Event</label>
                    <Dd
                      value={r.trigger}
                      onChange={(v) => setRule(r.id, { trigger: v })}
                      searchable={false}
                      ariaLabel="Trigger Event"
                      options={[
                        { value: 'intake_started', label: 'Intake started' },
                        { value: 'admission', label: 'Client admitted' },
                        { value: 'treatment_plan_signed', label: 'Treatment plan signed' },
                        { value: 'manual_only', label: 'Manual only' },
                      ]}
                    />
                  </div>
                  <div className="field-group" style={{ minWidth: 140, maxWidth: 180 }}>
                    <label>Send To</label>
                    <Dd
                      value={r.target}
                      onChange={(v) => setRule(r.id, { target: v })}
                      searchable={false}
                      ariaLabel="Send To"
                      options={[
                        { value: 'portal', label: 'Client Portal' },
                        { value: 'queue', label: 'Clinician Queue' },
                        { value: 'both', label: 'Both' },
                      ]}
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        <button
          className="fh-btn sm"
          style={{ marginTop: 10 }}
          onClick={() =>
            setWf((w) => ({
              ...w,
              autoRules: [
                ...w.autoRules,
                { id: `ar${Date.now()}`, on: true, trigger: 'intake_started', target: 'portal' },
              ],
            }))
          }
        >
          + Add Rule
        </button>
      </div>
    </Modal>
  );
}

/* ==========================================================
   Add-rule workspace (mockup addRuleView port)
   ========================================================== */
function RuleBuilder({ editId, onSaved }: { editId: string | null; onSaved: () => void }) {
  const forms = useFetch<RuleFormRow[]>(() => api.get('/forms'), []);
  const packets = useFetch<RuleBundleRow[]>(() => api.get('/packets'), []);
  const orgUsers = useFetch<OrgUser[]>(() => api.get('/settings/users'), []);
  const contactTypes = useFetch<Named[]>(() => api.get('/lookups/contact-types'), []);
  const referralSources = useFetch<Named[]>(() => api.get('/lookups/referral-sources'), []);
  const providers = useFetch<Named[]>(() => api.get('/lookups/providers'), []);
  const programs = useFetch<Named[]>(() => api.get('/lookups/programs'), []);

  const catalog: CatalogItem[] = useMemo(() => {
    // credifyfbs#302: same-named bundles each carry a hint that tells them apart.
    const hints = bundleHints(packets.data ?? []);
    return [
      // Only system forms that exist in the Form Builder catalog, one per name (credifyfbs#301:
      // the practice's edited copy of a built-in form no longer lists it a second time).
      ...ruleCatalogForms(forms.data ?? []).map((f) => ({
        id: f.id,
        name: f.name,
        type: 'form' as const,
        desc: f.description ?? '',
      })),
      ...(packets.data ?? []).map((p) => ({
        id: p.id,
        name: p.name,
        type: 'packet' as const,
        desc: p.description ?? '',
        hint: hints.get(p.id),
      })),
    ];
  }, [forms.data, packets.data]);

  const [selected, setSelected] = useState<CatalogItem[]>([]);
  const [formSearch, setFormSearch] = useState('');
  // #106 - open the shared Bundle editor in-context from a bundle row.
  const { user } = useSession();
  const manage = canManage(user?.role);
  const [bundleEd, setBundleEd] = useState<{
    open: boolean;
    mode: 'new' | 'edit' | 'saveas';
    id: string | null;
  }>({ open: false, mode: 'edit', id: null });
  // Member forms of any selected packet — rules link forms only, so packets
  // expand to their member formIds at save time (Patient≠Contact-style backend
  // constraint: RuleForm rows reference Form ids).
  const [packetMembers, setPacketMembers] = useState<Record<string, string[]>>({});
  const [recClient, setRecClient] = useState(true);
  const [recClinician, setRecClinician] = useState(false);
  const [recRole, setRecRole] = useState(false);
  const [roleValue, setRoleValue] = useState('');
  const [recUser, setRecUser] = useState(false);
  const [userValue, setUserValue] = useState('');
  const [conds, setConds] = useState<Cond[]>([newCond(true, 'c1')]);
  const nextCondId = useRef(2);
  const [deadline, setDeadline] = useState<Deadline>(emptyDeadline());
  const [fContactType, setFContactType] = useState('');
  const [fReferralSource, setFReferralSource] = useState('');
  const [fProvider, setFProvider] = useState('');
  const [fProgram, setFProgram] = useState('');
  const [name, setName] = useState('');
  const [nameEdited, setNameEdited] = useState(false);
  const [so, setSo] = useState<SignoffObligation>(defaultSO());
  const [soOpen, setSoOpen] = useState(false);
  const [status, setStatus] = useState('active');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(Boolean(editId));
  const [error, setError] = useState('');

  /* Auto-suggest rule name from forms + first trigger (mockup autoSuggestRuleName). */
  useEffect(() => {
    if (nameEdited) return;
    const formPart = selected.length ? selected.map((f) => f.name).join(' + ') : null;
    const trigPart = conds.length ? condLabel(conds[0]!) : '';
    setName([formPart, trigPart].filter(Boolean).join(' — '));
  }, [selected, conds, nameEdited]);

  /* Load rule when editing. */
  useEffect(() => {
    if (!editId) return;
    setLoading(true);
    api
      .get<ApiRule>(`/rules/${editId}`)
      .then((r) => {
        setName(r.name);
        setNameEdited(true);
        setStatus(r.status);
        setSelected(
          r.forms.map((f) => ({
            id: f.form.id,
            name: f.form.name,
            type: 'form' as const,
            desc: '',
          })),
        );
        setConds(
          r.conditions.length
            ? r.conditions.map((c, i) => {
                const isAppt = NEEDS_APPT.has(c.type);
                const apptType = isAppt
                  ? (APPT_TYPES.find((o) => o.label === c.event)?.value ?? 'every')
                  : 'every';
                return {
                  id: `c${i + 1}`,
                  type: c.type,
                  event: !isAppt && !NEEDS_DATE.has(c.type) ? (c.event ?? '') : '',
                  amount: c.unit === 'minutes' ? 0 : (c.amount ?? 3),
                  minutes: c.unit === 'minutes' ? (c.amount ?? 0) : 0,
                  apptType,
                  dateVal: NEEDS_DATE.has(c.type) ? (c.event ?? '') : '',
                  joiner: i === 0 ? null : c.joiner === 'OR' ? 'OR' : 'AND',
                  recurring: !!c.recurring,
                  recObj: c.recurring
                    ? { ...defaultRec(), ...(c.recurring as Partial<RecObj>) }
                    : null,
                };
              })
            : [newCond(true, 'c1')],
        );
        nextCondId.current = r.conditions.length + 1;
        const rec = r.recipients ?? [];
        setRecClient(rec.some((x) => x.kind === 'client'));
        setRecClinician(rec.some((x) => x.kind === 'clinician'));
        const role = rec.find((x) => x.kind === 'role');
        setRecRole(!!role);
        setRoleValue(role?.value ?? '');
        const usr = rec.find((x) => x.kind === 'user');
        setRecUser(!!usr);
        setUserValue(usr?.value ?? '');
        const fby = (dim: string) =>
          (r.filters ?? []).find((f) => f.dimension === dim)?.value ?? '';
        setFContactType(fby('contact_type'));
        setFReferralSource(fby('referral_source'));
        setFProvider(fby('provider'));
        setFProgram(fby('program'));
        if (r.deadline) setDeadline({ ...emptyDeadline(), ...r.deadline });
        if (r.signoffObligation) setSo({ ...defaultSO(), ...r.signoffObligation });
      })
      .catch(() => setError('Could not load this rule'))
      .finally(() => setLoading(false));
  }, [editId]);

  const toggleSelection = (item: CatalogItem) => {
    setSelected((sel) => {
      const has = sel.some((f) => f.id === item.id);
      const next = has ? sel.filter((f) => f.id !== item.id) : [...sel, item];
      if (!next.length) setNameEdited(false);
      return next;
    });
    if (item.type === 'packet' && !packetMembers[item.id]) {
      api
        .get<{ packetForms: { form: { id: string } }[] }>(`/packets/${item.id}`)
        .then((p) =>
          setPacketMembers((m) => ({ ...m, [item.id]: p.packetForms.map((pf) => pf.form.id) })),
        )
        .catch(() => setPacketMembers((m) => ({ ...m, [item.id]: [] })));
    }
  };

  const setCond = (id: string, patch: Partial<Cond>) =>
    setConds((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const addCondition = () => {
    setConds((cs) => [...cs, newCond(cs.length === 0, `c${nextCondId.current++}`)]);
  };
  const removeCondition = (id: string) =>
    setConds((cs) => {
      const next = cs.filter((c) => c.id !== id);
      if (next.length && next[0]!.joiner) next[0] = { ...next[0]!, joiner: null };
      return next;
    });

  const q = formSearch.trim().toLowerCase();
  const results = q
    ? catalog.filter((f) => f.name.toLowerCase().includes(q) || f.desc.toLowerCase().includes(q))
    : catalog;
  const selIds = new Set(selected.map((f) => f.id));
  // credifyfbs#303: Save Rule stays off until a form or bundle, a recipient and a trigger are set,
  // with a short line saying what is still missing.
  const saveMissing = ruleSaveMissing({
    selected: selected.length,
    recClient,
    recClinician,
    recRole,
    recUser,
    userValue,
    triggerTypes: conds.map((c) => c.type),
  });
  const saveHint = ruleSaveHint(saveMissing);

  async function save() {
    setError('');
    if (saveMissing.length) {
      setError(saveHint);
      return;
    }
    if (!name.trim()) {
      setError('Please enter a rule name.');
      return;
    }
    if (!selected.length) {
      setError('Please select at least one form or bundle.');
      return;
    }
    if (!conds.length) {
      setError('Please add at least one trigger condition.');
      return;
    }
    if (conds.some((c) => !c.type)) {
      setError('Please choose a trigger condition.');
      return;
    }
    if (deadline.enabled && isZeroDeadline(deadline)) {
      setError('Completion deadline must be at least 1 minute.');
      return;
    }
    const formIds = [
      ...new Set(
        selected.flatMap((s) => (s.type === 'form' ? [s.id] : (packetMembers[s.id] ?? []))),
      ),
    ];
    const recipients: { kind: string; value?: string }[] = [];
    if (recClient) recipients.push({ kind: 'client' });
    if (recClinician) recipients.push({ kind: 'clinician' });
    if (recRole) recipients.push({ kind: 'role', value: roleValue || 'clinician' });
    if (recUser && userValue) recipients.push({ kind: 'user', value: userValue });
    const filters: { dimension: string; value: string }[] = [];
    if (fContactType) filters.push({ dimension: 'contact_type', value: fContactType });
    if (fReferralSource) filters.push({ dimension: 'referral_source', value: fReferralSource });
    if (fProvider) filters.push({ dimension: 'provider', value: fProvider });
    if (fProgram) filters.push({ dimension: 'program', value: fProgram });
    const conditions = conds.map((c, i) => {
      const isAppt = NEEDS_APPT.has(c.type);
      const event = isAppt
        ? APPT_TYPES.find((o) => o.value === c.apptType)?.label
        : NEEDS_DATE.has(c.type)
          ? c.dateVal || undefined
          : NEEDS_EVENT.has(c.type) && c.event
            ? c.event
            : undefined;
      // The API stores one amount+unit; days win, a minutes-only offset is
      // persisted as unit:'minutes' (mockup shows both selects side by side).
      const offset = NEEDS_OFFSET.has(c.type)
        ? c.amount > 0
          ? { amount: c.amount, unit: 'days' as const }
          : { amount: c.minutes, unit: 'minutes' as const }
        : {};
      return {
        type: c.type,
        event,
        ...offset,
        direction: NEEDS_OFFSET.has(c.type)
          ? c.type === 'offset_before' || c.type === 'appt_before'
            ? ('before' as const)
            : ('after' as const)
          : undefined,
        recurring: c.recurring ? { ...(c.recObj ?? defaultRec()) } : undefined,
        joiner: i === 0 ? ('AND' as const) : (c.joiner ?? ('AND' as const)),
        position: i,
      };
    });
    const payload = {
      name: name.trim(),
      status: editId ? status : 'active',
      formIds,
      conditions,
      recipients,
      filters,
      deadline: deadline.enabled ? deadline : null,
      signoffObligation: so.enabled ? so : null,
    };
    setBusy(true);
    try {
      if (editId) await api.patch(`/rules/${editId}`, payload);
      else await api.post('/rules', payload);
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the rule');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="muted">Loading rule…</p>;

  return (
    <div id="addRuleView">
      <div
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, alignItems: 'start' }}
      >
        {/* LEFT COL */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Section 1: Form Search */}
          <div className="fh-card" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="fh-card-head">
              <h3>
                <CircleNum n={1} /> Select Forms or Bundles
              </h3>
            </div>
            <div
              className="card-body"
              style={{ display: 'flex', flexDirection: 'column', paddingBottom: 16 }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                  marginBottom: 10,
                  minHeight: 8,
                }}
              >
                {selected.map((f) => (
                  <span
                    className={`form-chip ${f.type === 'packet' ? 'chip-type-packet' : ''}`}
                    key={f.id}
                    title={f.hint}
                  >
                    {f.name}
                    <span
                      className="chip-remove"
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleSelection(f)}
                      onKeyDown={(e) => e.key === 'Enter' && toggleSelection(f)}
                    >
                      ✕
                    </span>
                  </span>
                ))}
              </div>
              <div className="form-search-box">
                <span className="form-search-icon">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </span>
                <input
                  type="text"
                  className="form-search-input"
                  placeholder="Search forms and bundles…"
                  autoComplete="off"
                  value={formSearch}
                  onChange={(e) => setFormSearch(e.target.value)}
                />
              </div>
              <div
                className="form-results"
                style={{ maxHeight: 'calc(100vh - 430px)', overflowY: 'auto' }}
              >
                {results.length === 0 ? (
                  <div className="form-search-empty">No forms match &quot;{formSearch}&quot;</div>
                ) : (
                  results.map((f) => (
                    <div className="form-result-item" key={f.id} onClick={() => toggleSelection(f)}>
                      <input
                        type="checkbox"
                        checked={selIds.has(f.id)}
                        tabIndex={-1}
                        onChange={() => toggleSelection(f)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="form-result-name">{f.name}</div>
                      <div className="form-result-meta">
                        {[f.desc, f.hint].filter(Boolean).join(' · ')}
                      </div>
                      <span
                        className={`form-result-type ${f.type === 'packet' ? 'frt-packet' : 'frt-form'}`}
                      >
                        {f.type === 'packet' ? 'Bundle' : 'Form'}
                      </span>
                      {f.type === 'packet' && (
                        <button
                          type="button"
                          className="btn sm"
                          style={{ marginLeft: 8 }}
                          title="Edit this bundle"
                          onClick={(e) => {
                            e.stopPropagation();
                            setBundleEd({ open: true, mode: 'edit', id: f.id });
                          }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Section 2: Recipients */}
          <div className="fh-card">
            <div className="fh-card-head">
              <h3>
                <CircleNum n={2} /> Recipients
              </h3>
            </div>
            <div className="card-body">
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
                Who receives this form when the rule fires?
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                <label className="channel-chk">
                  <input
                    type="checkbox"
                    checked={recClient}
                    onChange={(e) => setRecClient(e.target.checked)}
                  />{' '}
                  Client (via portal)
                </label>
                <label className="channel-chk">
                  <input
                    type="checkbox"
                    checked={recClinician}
                    onChange={(e) => setRecClinician(e.target.checked)}
                  />{' '}
                  Assigned Clinician
                </label>
                <label className="channel-chk">
                  <input
                    type="checkbox"
                    checked={recRole}
                    onChange={(e) => setRecRole(e.target.checked)}
                  />{' '}
                  Specific Role
                </label>
                <label className="channel-chk">
                  <input
                    type="checkbox"
                    checked={recUser}
                    onChange={(e) => setRecUser(e.target.checked)}
                  />{' '}
                  Specific User
                </label>
              </div>
              {recRole && (
                <div style={{ marginTop: 10 }}>
                  <div className="field-group" style={{ maxWidth: 260 }}>
                    <label>Role</label>
                    <Dd
                      value={roleValue}
                      onChange={setRoleValue}
                      searchable={false}
                      ariaLabel="Role"
                      placeholder="Select role…"
                      options={[
                        { value: '', label: 'Select role…' },
                        ...ROLE_OPTIONS.map((r) => ({ value: r.value, label: r.label })),
                      ]}
                    />
                  </div>
                </div>
              )}
              {recUser && (
                <div style={{ marginTop: 10 }}>
                  <div className="field-group" style={{ maxWidth: 260 }}>
                    <label>User</label>
                    <Dd
                      value={userValue}
                      onChange={setUserValue}
                      ariaLabel="User"
                      placeholder="Select user…"
                      options={[
                        { value: '', label: 'Select user…' },
                        ...(orgUsers.data ?? []).map((u) => ({
                          value: u.id,
                          label: orgUserName(u),
                        })),
                      ]}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COL */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Section 3: Trigger Conditions */}
          <div className="fh-card">
            <div className="fh-card-head">
              <h3>
                <CircleNum n={3} /> Trigger Conditions
              </h3>
            </div>
            <div className="card-body">
              <div className="condition-stack">
                {conds.map((cond, idx) => {
                  const showOffset = NEEDS_OFFSET.has(cond.type);
                  const showEvent = NEEDS_EVENT.has(cond.type);
                  const showAppt = NEEDS_APPT.has(cond.type);
                  const showDate = NEEDS_DATE.has(cond.type);
                  const showRecurring = !NO_RECURRING.has(cond.type);
                  const dir =
                    cond.type === 'offset_before' || cond.type === 'appt_before'
                      ? 'before'
                      : 'after';
                  const suggestions = TRIGGER_EVENT_SUGGESTIONS[cond.type] ?? [];
                  const evOpts =
                    cond.event && !suggestions.includes(cond.event)
                      ? [cond.event, ...suggestions]
                      : suggestions;
                  return (
                    <div key={cond.id}>
                      {idx > 0 && (
                        <div className="joiner-row">
                          <div className="joiner-pill">
                            <button
                              type="button"
                              className={`joiner-btn${cond.joiner === 'AND' ? ' active' : ''}`}
                              onClick={() => setCond(cond.id, { joiner: 'AND' })}
                            >
                              AND
                            </button>
                            <button
                              type="button"
                              className={`joiner-btn${cond.joiner === 'OR' ? ' active' : ''}`}
                              onClick={() => setCond(cond.id, { joiner: 'OR' })}
                            >
                              OR
                            </button>
                          </div>
                        </div>
                      )}
                      <div className="condition-row">
                        <div className="condition-row-head">
                          <div className="condition-row-num">{idx + 1}</div>
                          <Dd
                            value={cond.type}
                            onChange={(v) => setCond(cond.id, { type: v, event: '' })}
                            searchable={false}
                            ariaLabel="Trigger type"
                            placeholder="Select one…"
                            options={[
                              { value: '', label: 'Select one…' },
                              ...TRIGGER_TYPE_DEFS.map((t) => ({
                                value: t.value,
                                label: t.label,
                              })),
                            ]}
                          />
                          <button
                            type="button"
                            className="condition-remove"
                            title="Remove condition"
                            onClick={() => removeCondition(cond.id)}
                          >
                            ✕
                          </button>
                        </div>
                        <div className="condition-body">
                          {showEvent && !showAppt && (
                            <div
                              style={{
                                display: 'flex',
                                gap: 8,
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                marginBottom: 8,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 12.5,
                                  color: 'var(--text-muted)',
                                  fontWeight: 500,
                                }}
                              >
                                Program / Event:
                              </span>
                              <div style={{ minWidth: 200 }}>
                                <Dd
                                  value={cond.event}
                                  onChange={(v) => setCond(cond.id, { event: v })}
                                  ariaLabel="Program or event"
                                  placeholder={TRIGGER_EVENT_PLACEHOLDER[cond.type] ?? 'Specify…'}
                                  options={[
                                    {
                                      value: '',
                                      label: TRIGGER_EVENT_PLACEHOLDER[cond.type] ?? 'Specify…',
                                    },
                                    ...evOpts.map((s) => ({ value: s, label: s })),
                                  ]}
                                />
                              </div>
                            </div>
                          )}
                          {showAppt && (
                            <div
                              style={{
                                display: 'flex',
                                gap: 8,
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                marginBottom: 8,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 12.5,
                                  color: 'var(--text-muted)',
                                  fontWeight: 500,
                                }}
                              >
                                Appointment type:
                              </span>
                              <div style={{ minWidth: 210 }}>
                                <Dd
                                  value={cond.apptType}
                                  onChange={(v) => setCond(cond.id, { apptType: v })}
                                  searchable={false}
                                  ariaLabel="Appointment type"
                                  options={APPT_TYPES.map((o) => ({
                                    value: o.value,
                                    label: o.label,
                                  }))}
                                />
                              </div>
                            </div>
                          )}
                          {showDate && (
                            <div
                              style={{
                                display: 'flex',
                                gap: 8,
                                alignItems: 'center',
                                marginBottom: 8,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 12.5,
                                  color: 'var(--text-muted)',
                                  fontWeight: 500,
                                }}
                              >
                                Date:
                              </span>
                              <DateField
                                value={cond.dateVal}
                                onChange={(v) => setCond(cond.id, { dateVal: v })}
                              />
                            </div>
                          )}
                          {showOffset && (
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 6,
                                marginTop: 4,
                              }}
                            >
                              <div className="condition-offset-row">
                                <span
                                  style={{
                                    fontSize: 12.5,
                                    color: 'var(--text-muted)',
                                    fontWeight: 500,
                                  }}
                                >
                                  Send
                                </span>
                                <div style={{ minWidth: 130 }}>
                                  <Dd
                                    value={String(cond.amount)}
                                    onChange={(v) => setCond(cond.id, { amount: Number(v) })}
                                    ariaLabel="Offset days"
                                    options={DAY_DD_1}
                                  />
                                </div>
                                <span style={{ fontSize: 12, color: 'var(--text-muted-2)' }}>
                                  and
                                </span>
                                <div style={{ minWidth: 130 }}>
                                  <Dd
                                    value={String(cond.minutes)}
                                    onChange={(v) => setCond(cond.id, { minutes: Number(v) })}
                                    ariaLabel="Offset minutes"
                                    options={MIN_DD}
                                  />
                                </div>
                                <span
                                  style={{
                                    fontSize: 12.5,
                                    color: 'var(--text-muted)',
                                    fontWeight: 500,
                                  }}
                                >
                                  {dir}
                                </span>
                                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                                  {showAppt ? 'appointment' : 'event above'}
                                </span>
                              </div>
                            </div>
                          )}
                          {showRecurring && (
                            <div className="condition-recurring">
                              <label className="channel-chk" style={{ marginBottom: 0 }}>
                                <input
                                  type="checkbox"
                                  checked={cond.recurring}
                                  onChange={(e) =>
                                    setCond(cond.id, {
                                      recurring: e.target.checked,
                                      recObj: e.target.checked
                                        ? (cond.recObj ?? defaultRec())
                                        : cond.recObj,
                                    })
                                  }
                                />{' '}
                                <span
                                  style={{
                                    fontSize: 12.5,
                                    fontWeight: 500,
                                    color: 'var(--text-muted)',
                                  }}
                                >
                                  Recurring
                                </span>
                              </label>
                              {cond.recurring && (
                                <RecPanel
                                  value={cond.recObj ?? defaultRec()}
                                  onChange={(r) => setCond(cond.id, { recObj: r })}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Completion deadline */}
              <div className="deadline-row">
                <div className="deadline-row-main">
                  <input
                    type="checkbox"
                    className="deadline-chk"
                    checked={deadline.enabled}
                    onChange={(e) => setDeadline((d) => ({ ...d, enabled: e.target.checked }))}
                    aria-label="Enable completion deadline"
                  />
                  <span className="deadline-label">Must be completed within</span>
                  <div className={`deadline-cdds-wrap${deadline.enabled ? '' : ' disabled'}`}>
                    <div style={{ minWidth: 100 }}>
                      <Dd
                        value={String(deadline.days)}
                        onChange={(v) => setDeadline((d) => ({ ...d, days: Number(v) }))}
                        ariaLabel="Deadline days"
                        options={DAY_DD_0}
                      />
                    </div>
                    <div style={{ minWidth: 84 }}>
                      <Dd
                        value={String(deadline.hours)}
                        onChange={(v) => setDeadline((d) => ({ ...d, hours: Number(v) }))}
                        ariaLabel="Deadline hours"
                        options={HOUR_DD}
                      />
                    </div>
                    <div style={{ minWidth: 84 }}>
                      <Dd
                        value={String(deadline.mins)}
                        onChange={(v) => setDeadline((d) => ({ ...d, mins: Number(v) }))}
                        ariaLabel="Deadline minutes"
                        options={MIN_DD}
                      />
                    </div>
                  </div>
                </div>
                <div className="deadline-remind-row">
                  <input
                    type="checkbox"
                    className="deadline-chk"
                    checked={deadline.remindEnabled}
                    onChange={(e) =>
                      setDeadline((d) => ({ ...d, remindEnabled: e.target.checked }))
                    }
                    aria-label="Enable reminders"
                  />
                  <span className="deadline-label">Not completed, remind every</span>
                  <div className={`deadline-cdds-wrap${deadline.remindEnabled ? '' : ' disabled'}`}>
                    <div style={{ minWidth: 100 }}>
                      <Dd
                        value={String(deadline.remindDays)}
                        onChange={(v) => setDeadline((d) => ({ ...d, remindDays: Number(v) }))}
                        ariaLabel="Remind days"
                        options={DAY_DD_0}
                      />
                    </div>
                    <div style={{ minWidth: 84 }}>
                      <Dd
                        value={String(deadline.remindHours)}
                        onChange={(v) => setDeadline((d) => ({ ...d, remindHours: Number(v) }))}
                        ariaLabel="Remind hours"
                        options={HOUR_DD}
                      />
                    </div>
                    <div style={{ minWidth: 84 }}>
                      <Dd
                        value={String(deadline.remindMins)}
                        onChange={(v) => setDeadline((d) => ({ ...d, remindMins: Number(v) }))}
                        ariaLabel="Remind minutes"
                        options={MIN_DD}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                <button className="fh-btn sm" onClick={addCondition}>
                  + Add Condition
                </button>
              </div>
            </div>
          </div>

          {/* Section 4: Filters / Scope */}
          <div className="fh-card">
            <div className="fh-card-head">
              <h3>
                <CircleNum n={4} /> Applies To (Filters)
              </h3>
            </div>
            <div className="card-body">
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
                Scope this rule to specific contact types, referral sources, providers, or programs.
                Leave blank to apply to all.
              </div>
              <div className="field-row">
                <FilterSelect
                  label="Contact Type"
                  value={fContactType}
                  onChange={setFContactType}
                  options={contactTypes.data ?? []}
                />
                <FilterSelect
                  label="Referral Source"
                  value={fReferralSource}
                  onChange={setFReferralSource}
                  options={referralSources.data ?? []}
                />
              </div>
              <div className="field-row" style={{ marginBottom: 0 }}>
                <FilterSelect
                  label="Provider"
                  value={fProvider}
                  onChange={setFProvider}
                  options={providers.data ?? []}
                />
                <FilterSelect
                  label="Program / Service"
                  value={fProgram}
                  onChange={setFProgram}
                  options={programs.data ?? []}
                />
              </div>
            </div>
          </div>

          {/* Save Rule */}
          <div className="fh-card">
            <div
              className="card-body"
              style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
            >
              <div className="field-group" style={{ flex: 2, minWidth: 180 }}>
                <label>Rule Name</label>
                <input
                  type="text"
                  placeholder="Auto-suggested from form + trigger"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setNameEdited(true);
                  }}
                />
              </div>
              <div style={{ paddingTop: 22, display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  className="fh-btn"
                  title="Configure provider signoff obligation for this rule"
                  onClick={() => setSoOpen(true)}
                >
                  <PencilSvg />
                  Configure Signoff
                  {so.enabled && (
                    <span
                      style={{
                        background: 'var(--green-primary)',
                        color: '#fff',
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '1px 6px',
                        borderRadius: 999,
                        marginLeft: 4,
                      }}
                    >
                      ON
                    </span>
                  )}
                </button>
                <button
                  className="fh-btn primary"
                  onClick={save}
                  disabled={busy || saveMissing.length > 0}
                  title={saveHint || undefined}
                  aria-describedby={saveHint ? 'ruleSaveHint' : undefined}
                >
                  {busy ? 'Saving…' : editId ? 'Update Rule' : 'Save Rule'}
                </button>
              </div>
              {saveHint && !error && (
                <span id="ruleSaveHint" className="rule-save-hint">
                  {saveHint}
                </span>
              )}
              {error && (
                <span style={{ fontSize: 12.5, color: 'var(--red)', width: '100%' }}>{error}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {soOpen && (
        <SignoffObligationModal
          value={so}
          users={orgUsers.data ?? []}
          onClose={() => setSoOpen(false)}
          onApply={(next) => {
            setSo(next);
            setSoOpen(false);
          }}
        />
      )}
      {/* #106 - shared Bundle editor, opened in-context from a bundle row's Edit button.
          credifyfbs#300: it is only on the page while open. Kept on the page closed, it was
          merely slid off screen: still a "Bundle Builder" dialog to screen readers and the
          browser, with its fields in the Tab order, so Form Rules seemed to open with it. */}
      {bundleEd.open && (
        <BundleEditor
          open
          mode={bundleEd.mode}
          bundleId={bundleEd.id}
          manage={manage}
          onClose={() => setBundleEd((s) => ({ ...s, open: false }))}
          onSaved={() => packets.reload()}
        />
      )}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Named[];
}) {
  return (
    <div className="field-group">
      <label>{label}</label>
      <Dd
        value={value}
        onChange={onChange}
        ariaLabel={label}
        placeholder="Any"
        options={[
          { value: '', label: 'Any' },
          ...options.map((o) => ({ value: o.name, label: o.name })),
        ]}
      />
    </div>
  );
}

/* ==========================================================
   Saved rules view (mockup savedRulesView port)
   ========================================================== */
const REC_KIND_LABEL: Record<string, string> = {
  client: 'Client',
  clinician: 'Clinician',
  role: 'Role',
  user: 'User',
};
const recipientsSummary = (recipients: { kind: string }[]) =>
  recipients.map((r) => REC_KIND_LABEL[r.kind] ?? r.kind).join(', ') || 'Client';

function SavedRules({
  rules,
  manage,
  onEdit,
}: {
  rules: ReturnType<typeof useFetch<ApiRule[]>>;
  manage: boolean;
  onEdit: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function act(id: string, action: 'pause' | 'activate' | 'duplicate') {
    setBusy(id);
    try {
      await api.post(`/rules/${id}/${action}`);
      rules.reload();
    } catch (e) {
      showError(errorText(e, 'Could not update the rule.'));
    } finally {
      setBusy(null);
    }
  }
  async function del(rule: ApiRule) {
    if (!(await askConfirm(`Delete the rule "${rule.name}"? This can't be undone.`))) return;
    setBusy(rule.id);
    try {
      await api.del(`/rules/${rule.id}`);
      if (expanded === rule.id) setExpanded(null);
      rules.reload();
    } catch (e) {
      showError(errorText(e, 'Could not delete the rule.'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="fh-card">
      <div className="fh-card-head">
        <h3>Saved Rules</h3>
        <span className="pill green">{(rules.data ?? []).length} rules</span>
      </div>
      <Async state={rules} emptyWhen={(r) => r.length === 0} emptyMessage="No rules yet.">
        {(list) => (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Rule Name</th>
                  <th>Form(s)</th>
                  <th>Trigger Summary</th>
                  <th>Status</th>
                  <th>Last Fired</th>
                  <th>Next Scheduled</th>
                  <th>Recipients</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((rule) => {
                  const dl = rule.deadline
                    ? { ...emptyDeadline(), ...rule.deadline }
                    : emptyDeadline();
                  const summary =
                    rule.triggerSummary +
                    (dl.enabled ? ` · complete within ${deadlineLabel(dl)}` : '');
                  const open = expanded === rule.id;
                  const filterParts: string[] = [];
                  const fby = (dim: string, label: string) => {
                    const vals = rule.filters
                      .filter((f) => f.dimension === dim)
                      .map((f) => f.value);
                    if (vals.length) filterParts.push(`${label}: ${vals.join(', ')}`);
                  };
                  fby('contact_type', 'Contact');
                  fby('referral_source', 'Source');
                  fby('provider', 'Provider');
                  fby('program', 'Program');
                  return (
                    <FragmentRow
                      key={rule.id}
                      rule={rule}
                      open={open}
                      summary={summary}
                      filterParts={filterParts}
                      manage={manage}
                      busy={busy === rule.id}
                      onToggleExpand={() => setExpanded(open ? null : rule.id)}
                      onToggleStatus={() =>
                        act(rule.id, rule.status === 'active' ? 'pause' : 'activate')
                      }
                      onEdit={() => onEdit(rule.id)}
                      onDup={() => act(rule.id, 'duplicate')}
                      onDel={() => del(rule)}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Async>
    </div>
  );
}

function FragmentRow({
  rule,
  open,
  summary,
  filterParts,
  manage,
  busy,
  onToggleExpand,
  onToggleStatus,
  onEdit,
  onDup,
  onDel,
}: {
  rule: ApiRule;
  open: boolean;
  summary: string;
  filterParts: string[];
  manage: boolean;
  busy: boolean;
  onToggleExpand: () => void;
  onToggleStatus: () => void;
  onEdit: () => void;
  onDup: () => void;
  onDel: () => void;
}) {
  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={onToggleExpand}>
        <td>
          <span style={{ fontSize: 16, color: 'var(--text-muted-2)' }}>{open ? '▼' : '▶'}</span>
        </td>
        <td>
          <strong>{rule.name}</strong>
        </td>
        <td>
          {rule.forms
            .filter((f) => isSystemForm(f.form.name))
            .map((f) => (
              <span className="form-chip" style={{ marginRight: 3 }} key={f.form.id}>
                {f.form.name}
              </span>
            ))}
        </td>
        <td style={{ fontSize: 12.5, color: 'var(--text-muted)', maxWidth: 180 }}>{summary}</td>
        <td>
          <button
            type="button"
            className={`trigger-toggle${rule.status === 'active' ? ' on' : ''}`}
            title={
              rule.status === 'active' ? 'Active — click to pause' : 'Paused — click to activate'
            }
            aria-label="Toggle rule status"
            disabled={!manage || busy}
            onClick={(e) => {
              e.stopPropagation();
              onToggleStatus();
            }}
          />
        </td>
        <td style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>—</td>
        <td style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {rule.status === 'active' ? 'On next trigger' : '—'}
        </td>
        <td style={{ fontSize: 12.5 }}>{recipientsSummary(rule.recipients)}</td>
        <td>
          <div className="actions" onClick={(e) => e.stopPropagation()}>
            {manage ? (
              <>
                <button className="btn sm" disabled={busy} onClick={onEdit}>
                  Edit
                </button>
                <button className="btn sm" disabled={busy} onClick={onDup}>
                  Dup
                </button>
                <button className="btn sm danger" disabled={busy} onClick={onDel}>
                  Del
                </button>
              </>
            ) : (
              <span style={{ fontSize: 12, color: 'var(--text-muted-2)' }}>View only</span>
            )}
          </div>
        </td>
      </tr>
      <tr className={`tr-expand${open ? ' open' : ''}`}>
        <td colSpan={9}>
          <div className="rule-expand-detail">
            <div className="rule-expand-section">
              <strong>Trigger Conditions</strong>
              <div className="rule-expand-conds">
                {rule.conditions.map((c, i) => (
                  <div className="rule-expand-cond" key={i}>
                    {i > 0 && c.joiner && <span className="rec-joiner">{c.joiner}</span>}
                    <span>{condLabel(c)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rule-expand-section">
              <strong>Scope / Filters</strong>
              <span>{filterParts.length ? filterParts.join(' · ') : 'Applies to all'}</span>
            </div>
            <div className="rule-expand-section">
              <strong>Recipients</strong>
              <span>{recipientsSummary(rule.recipients)}</span>
            </div>
          </div>
        </td>
      </tr>
    </>
  );
}

/* ==========================================================
   Tab shell (mockup panel-formrules header + views)
   ========================================================== */
export function FormRulesTab() {
  const { user } = useSession();
  const manage = canManage(user?.role);
  const rules = useFetch<ApiRule[]>(() => api.get('/rules'), []);
  const [seg, setSeg] = useState<'add' | 'saved'>(manage ? 'add' : 'saved');
  const [editId, setEditId] = useState<string | null>(null);
  const [showWorkflow, setShowWorkflow] = useState(false);
  const builderKey = editId ?? 'new';

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 18,
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 2 }}>Form Rules</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Define when forms are automatically sent to clients, providers, or programs.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {manage && (
            <span
              id="ruleScopeIndicator"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 12,
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: 999,
                border: '1px solid var(--green-accent)',
                background: 'var(--mint-pale)',
                color: 'var(--green-primary)',
              }}
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>{' '}
              Org-wide
            </span>
          )}
          <button
            className="fh-btn"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
            onClick={() => setShowWorkflow(true)}
          >
            <BoltSvg /> Notifications &amp; Workflow
          </button>
          {manage && (
            <div className="seg-toggle" id="rulesSegToggle">
              <button
                className={`seg-btn${seg === 'add' ? ' active' : ''}`}
                onClick={() => {
                  setEditId(null);
                  setSeg('add');
                }}
              >
                + Add Form Rule
              </button>
              <button
                className={`seg-btn${seg === 'saved' ? ' active' : ''}`}
                onClick={() => setSeg('saved')}
              >
                Saved Rules
              </button>
            </div>
          )}
        </div>
      </div>

      {seg === 'add' && manage ? (
        <RuleBuilder
          key={builderKey}
          editId={editId}
          onSaved={() => {
            rules.reload();
            setEditId(null);
            setSeg('saved');
          }}
        />
      ) : (
        <SavedRules
          rules={rules}
          manage={manage}
          onEdit={(id) => {
            setEditId(id);
            setSeg('add');
          }}
        />
      )}

      {showWorkflow && <WorkflowModal onClose={() => setShowWorkflow(false)} canSave={manage} />}
    </>
  );
}
